import { type FC, useContext, useRef } from 'react';

import { useDay } from '@datepicker-react/hooks';
import { clsx } from 'clsx';

import { DatePickerContext } from '../../../../context';

interface DayProps {
  day: string;
  date: Date;
  isDark?: boolean;
}

const Day: FC<React.PropsWithChildren<DayProps>> = ({ day, date, isDark }) => {
  const dayRef = useRef(null);
  const {
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateSelect,
    onDateFocus,
    onDateHover,
  } = useContext(DatePickerContext);
  const {
    isSelected,
    isSelectedStartOrEnd,
    isWithinHoverRange,
    onClick,
    onKeyDown,
    onMouseEnter,
    tabIndex,
    disabledDate,
  } = useDay({
    date,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateFocus,
    onDateSelect,
    onDateHover,
    dayRef,
  });

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (!day) return <div />;

  return (
    <button
      type="button"
      onClick={onClick}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      onMouseEnter={onMouseEnter}
      ref={dayRef}
      className={clsx(
        'h-32 w-32 flex justify-center items-center focus:outline-none font-body4',
        isSelected || isSelectedStartOrEnd
          ? 'rounded-full text-white bg-primary-main font-subtitle4'
          : null,
        isWithinHoverRange
          ? 'rounded-full bg-primary-lighter text-white'
          : null,
        disabledDate && !isDark ? 'text-gray-main' : null,
        disabledDate && isDark ? 'text-gray-dark' : null,
      )}
    >
      {day}
    </button>
  );
};

export default Day;
